import React, { useState, useEffect } from 'react'
import { Typography, Button } from '../../../components'
import { Formik } from 'formik'
import * as Yup from 'yup'
import Input from '../../../components/Form/Input'
import { Form, FormBody, FormFooter } from '../../../components/Form/styles'
import { DefaultRegularPages } from '../../DefaultPages'
import useQueryParams from '../../../hooks/useQueryParams'

import { Container, SubContainer, Title } from './styles'
import { InputContainer } from '../styles'
import ErrorMessage from '../../../components/Form/ErrorMessage'
import { validateCPF } from 'validations-br'
import { onlyNumbers } from '../../../utils/onlyNumbers'
import ecbrApi from '../../../services/ecbrApi'
import useHistory from '../../../hooks/useHistory'
import { Roles } from '../../../constants/roles'
import useScrollToTop from '../../../hooks/useScrollToTop'
import { extractErrorMessage } from '../../../helpers/extractErrorMessage'

const initialValues = {
  name: '',
  lastname: '',
  document: '',
  cellphone: '',
  email: '',
  password: '',
  confirmPassword: ''
}

const formSchema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
  lastname: Yup.string().required('Campo obrigatório'),
  document: Yup.string()
    .required('Campo obrigatório')
    .test('document', 'CPF inválido', (value) => validateCPF(value)),
  cellphone: Yup.string()
    .required('Campo obrigatório')
    .test(
      'cellphone',
      'Celular inválido',
      (value) => onlyNumbers(value).length === 11
    ),
  email: Yup.string().required('Campo obrigatório').email('Email inválido'),
  password: Yup.string().required('Campo obrigatório'),
  confirmPassword: Yup.string()
    .required('Confirme sua senha')
    .oneOf([Yup.ref('password'), ''], 'Confirmação de senha incorreta')
})

const Invite = () => {
  const [error, setError] = useState('')
  const [registerFinished, setRegisterFinished] = useState(false)
  const [registeredUser, setRegisteredUser] = useState(null)
  const hash = useQueryParams().get('token')
  const history = useHistory()
  const { scrollToTop } = useScrollToTop()

  const onSubmit = async ({ cellphone, ...values }, { setSubmitting }) => {
    // TODO: Finalizar cadastro de usuário através de um convite usando a api ecbrApi.userInvites.completeRegistration(hash)
    try {
      setSubmitting(true)

      const data = {
        ...values,
        cellphone: onlyNumbers(cellphone)
      }

      const response = await ecbrApi.userInvites.completeRegistration(
        hash,
        data
      )

      // response.data example:
      // {
      //   name: 'Teste',
      //   lastname: 'Um',
      //   document: '085.801.880-24',
      //   email: 'teste@teste.com',
      //   password:
      //     '$2b$10$6LnUkrq.bXAzGAY1gxN2xOIwzQJJVXgMbUOIu2R.HJUxsqkWvQdC6',
      //   confirmPassword: 'teste',
      //   cellphone: '11996565656',
      //   role: 'ADMIN_ECBR',
      //   googleId: null,
      //   hashForgotPasswordExpiration: null,
      //   hashForgotPassword: null,
      //   statusSend: null,
      //   id: 3,
      //   active: true,
      //   createdAt: '2022-03-09T12:38:57.970Z',
      //   updatedAt: '2022-03-09T12:38:57.970Z'
      // }

      setRegisteredUser(response.data)
      setSubmitting(false)
      setRegisterFinished(true)
    } catch (err) {
      console.log({ err })
      const defaultMessage = 'Ocorreu um erro ao finalizar o cadastro'
      const errorMessage = extractErrorMessage(err, defaultMessage)
      setError(errorMessage)
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (registerFinished) {
      scrollToTop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFinished])

  const handleLogin = () => {
    const isEcbrUser = [Roles.ADMIN_ECBR, Roles.VIEWER_ECBR].includes(
      registeredUser.role
    )
    history.push(isEcbrUser ? '/admin/login' : '/login')
  }

  if (!hash) {
    return (
      <DefaultRegularPages>
        <Container>
          <SubContainer>
            <Title>
              <Typography variant="h1">
                Você não tem permissão para acessar essa página
              </Typography>
            </Title>
          </SubContainer>
        </Container>
      </DefaultRegularPages>
    )
  }

  return (
    <DefaultRegularPages>
      <Container>
        <SubContainer>
          <Title>Cadastro</Title>
          {registerFinished ? (
            <>
              <Typography
                type="text"
                color="#7D7987"
                spacing="1rem 0 1.5rem 0"
                align="center"
              >
                Cadastro finalizado com sucesso!
              </Typography>
              <Typography
                type="text"
                color="#7D7987"
                spacing="0 0 1.5rem 0"
                align="center"
              >
                Agora você pode fazer login com seu usuário e senha.
              </Typography>
              <Button
                text="FAZER LOGIN"
                onClick={handleLogin}
                type="button"
                variant="primary"
                width="200px"
              />
            </>
          ) : (
            <>
              <Typography
                type="text"
                color="#7D7987"
                spacing="1rem 0 1.5rem 0"
                align="center"
              >
                Precisamos de alguns dados para finalizar o seu cadastro
              </Typography>
              <Formik
                initialValues={initialValues}
                validationSchema={formSchema}
                onSubmit={onSubmit}
              >
                {({ isSubmitting }) => (
                  <Form maxWidth="100%">
                    <FormBody>
                      <InputContainer>
                        <Input
                          name="name"
                          label="Nome"
                          showValidIcon
                          display="inline-block"
                        />
                        <Input
                          name="lastname"
                          label="Sobrenome"
                          showValidIcon
                          display="inline-block"
                        />
                      </InputContainer>
                      <Input
                        name="document"
                        label="CPF"
                        showValidIcon
                        mask="999.999.999-99"
                      />
                      <Input
                        name="cellphone"
                        label="Celular"
                        showValidIcon
                        mask="(99) 99999-9999"
                      />
                      <Input name="email" label="Email" showValidIcon />
                      <Input name="password" type="password" label="Senha" />
                      <Input
                        name="confirmPassword"
                        type="password"
                        label="Confirmar senha"
                      />
                      <ErrorMessage>{error}</ErrorMessage>
                    </FormBody>
                    <FormFooter>
                      <Button
                        text="CADASTRAR"
                        type="submit"
                        variant="primary"
                        width="200px"
                        disabled={isSubmitting}
                      />
                    </FormFooter>
                  </Form>
                )}
              </Formik>
            </>
          )}
        </SubContainer>
      </Container>
    </DefaultRegularPages>
  )
}

export default Invite
